/**
 * DeviceMapPanel — SAAS-TENANT-PORTAL-MAP-1 + SAAS-COMPANIES-MAP-CENTER-SCHEMA-1.
 *
 * Painel de mapa reaproveitável, usado em DOIS lugares:
 *   - área admin Empresas → detalhe do tenant (recebe `tenantId` + `tenant`,
 *     platform admin filtra por empresa via ?tenant_id=);
 *   - portal do cliente → página Mapa (SEM `tenantId`; o backend escopa por
 *     ctx.tenant_id → cada tenant só vê a própria frota; a empresa vem do whoami).
 *
 * Fetch = getFleetMapDevices({ tenantId }); quando tenantId é undefined a
 * querystring não leva tenant_id (segurança: nunca X-Tenant; o escopo é do Bearer).
 *
 * Centro do mapa (MAP-CENTER-SCHEMA-1): a FONTE PRINCIPAL passa a ser o banco
 * (`tenant.map_center`, compartilhado entre operadores). Precedência de exibição:
 *   sessão (recém-salvo) → banco → localStorage (fallback fase-1) → fit-bounds.
 * Quem tem `tenant:manage` (ou platform) salva o centro da EMPRESA via
 * PATCH /tenants/{id} (persistido + auditado); os demais só visualizam.
 */
import { useEffect, useMemo, useState } from "react";
import { getFleetMapDevices, getWhoAmI, updateTenant } from "../../lib/api.js";
import TenantMiniMap from "./TenantMiniMap.jsx";

function Kpi({ label, value, tone }) {
  return (
    <div className="glass-card-pad">
      <div className={`text-2xl font-bold ${tone || ""}`}>{value}</div>
      <div className="text-xs text-noc-muted">{label}</div>
    </div>
  );
}

export default function DeviceMapPanel({ tenantId, tenant, onNavigate }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // all|online|offline|protected|no_geo
  const [query, setQuery] = useState("");

  const [whoami, setWhoami] = useState(null);
  const [viewport, setViewport] = useState(null);
  const [showCenterModal, setShowCenterModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  // undefined = não mexido nesta sessão; {..} = salvo agora; null = limpo agora
  const [sessionCenter, setSessionCenter] = useState(undefined);

  const CENTER_KEY = `mapCenter:${tenantId || "self"}`;
  const [localCenter] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CENTER_KEY) || "null"); }
    catch { return null; }
  });

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true); setErr(null);
    getFleetMapDevices({ tenantId, signal: ac.signal })
      .then((d) => setDevices(d.devices || []))
      .catch((e) => { if (e?.name !== "AbortError") setErr(e?.message || "Falha ao carregar o mapa."); })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [tenantId]);

  // whoami — para saber a empresa (portal) e se o usuário pode salvar o centro.
  useEffect(() => {
    const ac = new AbortController();
    getWhoAmI({ signal: ac.signal })
      .then(setWhoami)
      .catch(() => { /* sem whoami → some o botão de salvar; mapa segue */ });
    return () => ac.abort();
  }, []);

  // Empresa-alvo do centro: o `tenant` (admin) ou a empresa do whoami (portal).
  const mapTenant = tenant || whoami?.tenant || null;
  const saveTenantId = tenant?.id || whoami?.tenant?.id || null;
  const caps = whoami?.capabilities || [];
  const isPlatform = caps.includes("platform:admin");
  // pode salvar: platform (qualquer empresa) OU tenant:manage na PRÓPRIA empresa.
  const canManage = isPlatform
    || (caps.includes("tenant:manage") && !!saveTenantId
        && whoami?.tenant?.id === saveTenantId);

  // Centro do banco (compartilhado) quando lat/lng existem.
  const backendCenter = useMemo(() => {
    const mc = mapTenant?.map_center;
    if (mc && typeof mc.lat === "number" && typeof mc.lng === "number") {
      return { lat: mc.lat, lng: mc.lng, zoom: mc.zoom || 12, label: mc.label || null };
    }
    return null;
  }, [mapTenant]);

  // Precedência: sessão (recém-salvo/limpo) → banco → localStorage → null.
  const center = sessionCenter !== undefined
    ? sessionCenter
    : (backendCenter || localCenter);
  const centerFromCompany = sessionCenter !== undefined
    ? (sessionCenter ? true : false)
    : (backendCenter ? true : false);

  const hasGeo = (d) => typeof d.lat === "number" && typeof d.lng === "number";

  // KPIs (sobre TODA a frota do escopo, não só a filtrada)
  const kpi = useMemo(() => {
    const total = devices.length;
    const geo = devices.filter(hasGeo).length;
    const online = devices.filter((d) => d.online).length;
    return { total, geo, noGeo: total - geo, online, offline: total - online };
  }, [devices]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return devices.filter((d) => {
      if (statusFilter === "online" && !d.online) return false;
      if (statusFilter === "offline" && d.online) return false;
      if (statusFilter === "protected" && !d.effective_protected) return false;
      if (statusFilter === "no_geo" && hasGeo(d)) return false;
      if (!q) return true;
      return [d.device_id, d.customer_name, d.mac]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [devices, statusFilter, query]);

  const noGeo = visible.filter((d) => !hasGeo(d));
  const geoCount = visible.length - noGeo.length;

  async function saveCenter(c) {
    setSaveMsg(null);
    const payload = { label: c.label ?? null, lat: c.lat, lng: c.lng, zoom: c.zoom };
    if (canManage && saveTenantId) {
      try {
        setSaving(true);
        await updateTenant(saveTenantId, { map_center: payload });
        setSessionCenter({ lat: c.lat, lng: c.lng, zoom: c.zoom, label: c.label ?? null });
        setSaveMsg("Centro da empresa salvo — visível a todos os operadores.");
        setShowCenterModal(false);
      } catch (e) {
        setSaveMsg(e?.message || "Falha ao salvar o centro da empresa.");
      } finally { setSaving(false); }
    } else {
      // fallback fase-1 (sem permissão de salvar no backend): só neste navegador.
      localStorage.setItem(CENTER_KEY, JSON.stringify(c));
      setSessionCenter(c);
      setShowCenterModal(false);
    }
  }

  async function clearCenter() {
    setSaveMsg(null);
    if (canManage && saveTenantId) {
      try {
        setSaving(true);
        await updateTenant(saveTenantId, { map_center: null });
        setSessionCenter(null);
        setSaveMsg("Centro da empresa removido — o mapa volta a enquadrar a frota.");
        setShowCenterModal(false);
      } catch (e) {
        setSaveMsg(e?.message || "Falha ao remover o centro da empresa.");
      } finally { setSaving(false); }
    } else {
      localStorage.removeItem(CENTER_KEY);
      setSessionCenter(null);
      setShowCenterModal(false);
    }
  }

  if (loading) {
    return <div className="text-sm text-noc-muted glass-card-pad">Carregando mapa…</div>;
  }
  if (err) {
    return <div className="text-sm text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg p-3">{err}</div>;
  }

  // SAAS-TENANT-PORTAL-MAP-CENTER-FIX-1 — NÃO fazer early-return no 0-devices:
  // isso escondia a toolbar e o botão "Centro da empresa", então um tenant com
  // 0 devices (ex.: Fibra Net - Quijingue) nunca conseguia definir a cidade
  // central. Agora o painel sempre renderiza toolbar + controles de centro; o
  // estado vazio vira um bloco com mensagem + CTA de centro + mapa neutro.

  return (
    <div className="space-y-3">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Kpi label="Total" value={kpi.total} />
        <Kpi label="Com localização" value={kpi.geo} />
        <Kpi label="Sem localização" value={kpi.noGeo} />
        <Kpi label="Online" value={kpi.online} tone="text-noc-ok" />
        <Kpi label="Offline" value={kpi.offline} tone="text-noc-muted" />
      </div>

      {saveMsg && (
        <div className="text-xs text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded-lg px-3 py-2">
          {saveMsg}
        </div>
      )}

      {/* toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex rounded-lg border border-noc-border overflow-hidden text-xs w-fit flex-wrap">
          {[["all", `Todos (${visible.length})`], ["online", "Online"],
            ["offline", "Offline"], ["protected", "Protegidos"],
            ["no_geo", "Sem localização"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setStatusFilter(k)}
              className={`px-2.5 py-1 ${statusFilter === k
                ? "bg-noc-accent/20 text-noc-accent font-semibold"
                : "text-noc-muted hover:text-noc-text"}`}>
              {label}
            </button>
          ))}
        </div>
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar device_id / cliente / MAC…"
          className="h-8 px-2.5 rounded-lg bg-noc-bg border border-noc-border text-xs text-noc-text sm:w-56" />
        <div className="flex-1" />
        {canManage && (
          <button type="button" onClick={() => { setSaveMsg(null); setShowCenterModal(true); }}
            className="h-8 px-3 rounded-lg text-xs border border-noc-border text-noc-text">
            📍 Centro da empresa
          </button>
        )}
      </div>

      {geoCount === 0 ? (
        <div className="space-y-3">
          <div className="glass-card-pad text-center py-6 space-y-2">
            <div className="text-sm text-noc-muted">
              Nenhum dispositivo com localização cadastrada ainda.
            </div>
            <div className="text-[11px] text-noc-muted/70">
              {devices.length === 0
                ? "Assim que um equipamento for adicionado e enviar heartbeat, ele aparece aqui. Enquanto isso, você já pode definir a cidade central do mapa da empresa."
                : `Os ${visible.length} dispositivo(s) desta empresa não têm coordenada — cadastre a localização no detalhe de cada device para vê-los no mapa.`}
            </div>
            <div className="flex flex-wrap gap-2 justify-center pt-1">
              {onNavigate && (
                <button type="button" onClick={() => onNavigate("claim-device")}
                  className="h-8 px-3 rounded-lg text-xs font-semibold bg-noc-accent/15 text-noc-accent border border-noc-accent/40">
                  + Adicionar dispositivo
                </button>
              )}
              {canManage && (
                <button type="button" onClick={() => { setSaveMsg(null); setShowCenterModal(true); }}
                  className="h-8 px-3 rounded-lg text-xs font-semibold border border-noc-border text-noc-text">
                  📍 Definir cidade central
                </button>
              )}
            </div>
          </div>
          {/* mapa neutro (Bahia/fallback ou centro salvo) p/ o gestor
              posicionar visualmente e usar "capturar visualização atual" */}
          {(canManage || center) && (
            <TenantMiniMap
              devices={[]}
              center={center}
              onSelect={(deviceId) => onNavigate?.("detail", deviceId)}
              onViewportChange={setViewport}
            />
          )}
        </div>
      ) : (
        <TenantMiniMap
          devices={visible}
          center={center}
          onSelect={(deviceId) => onNavigate?.("detail", deviceId)}
          onViewportChange={setViewport}
        />
      )}

      <div className="text-[11px] text-noc-muted flex flex-wrap gap-x-4 gap-y-1">
        <span>{geoCount} no mapa · {noGeo.length} sem localização</span>
        {center && centerFromCompany && (
          <span>
            Centro da empresa{center.label ? `: ${center.label}` : ` (${center.lat}, ${center.lng})`}
          </span>
        )}
        {center && !centerFromCompany && (
          <span>Centro salvo neste navegador ({center.lat}, {center.lng})</span>
        )}
      </div>

      {/* sem localização */}
      {noGeo.length > 0 && (
        <div className="glass-card-pad">
          <div className="text-xs font-semibold uppercase tracking-wide text-noc-muted mb-2">
            Sem localização ({noGeo.length})
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {noGeo.map((d) => (
              <button key={d.device_id} type="button"
                onClick={() => onNavigate?.("detail", d.device_id)}
                className="w-full text-left flex items-center gap-2 text-xs hover:bg-noc-bg/40 rounded px-1 py-0.5">
                <span className={`w-2 h-2 rounded-full shrink-0 ${d.online ? "bg-noc-ok" : "bg-noc-muted"}`} />
                <span className="font-mono">{d.device_id}</span>
                {d.effective_protected && <span className="text-noc-purple">🔒</span>}
                <span className="text-noc-muted truncate">{d.customer_name || ""}</span>
                <span className="flex-1" />
                <span className="text-noc-accent">abrir detalhe →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {showCenterModal && (
        <MapCenterModal
          current={viewport || center}
          saving={saving}
          errorMsg={saveMsg}
          onSave={saveCenter}
          onClear={center ? clearCenter : null}
          onClose={() => setShowCenterModal(false)}
        />
      )}
    </div>
  );
}

/* ──── Modal do centro do mapa (persistido na empresa via PATCH /tenants) ──── */
function MapCenterModal({ current, saving, errorMsg, onSave, onClear, onClose }) {
  const [label, setLabel] = useState(current?.label ?? "");
  const [lat, setLat] = useState(current?.lat ?? "");
  const [lng, setLng] = useState(current?.lng ?? "");
  const [zoom, setZoom] = useState(current?.zoom ?? 13);

  function submit() {
    const la = Number(lat), ln = Number(lng), zo = Number(zoom);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
    onSave({
      label: label.trim() || null,
      lat: +la.toFixed(5),
      lng: +ln.toFixed(5),
      zoom: Math.min(20, Math.max(3, zo || 13)),
    });
  }

  return (
    <div className="fixed inset-0 z-[2100] bg-black/70 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="bg-noc-panel border border-noc-accent/30 rounded-lg shadow-2xl w-full max-w-sm p-4 space-y-3">
        <div className="text-sm font-semibold text-noc-accent">Centro do mapa da empresa</div>
        <p className="text-[11px] text-noc-muted">
          Latitude/longitude do centro inicial do mapa. Salvo <b>na empresa</b> —
          compartilhado entre todos os operadores. Use o botão abaixo para
          capturar a posição atual do mapa.
        </p>
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Cidade / rótulo (opcional)</span>
          <input className="installer-input" value={label}
            onChange={(e) => setLabel(e.target.value)} placeholder="Quijingue-BA" maxLength={120} />
        </label>
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Latitude</span>
          <input className="installer-input" value={lat}
            onChange={(e) => setLat(e.target.value)} placeholder="-10.6657" />
        </label>
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Longitude</span>
          <input className="installer-input" value={lng}
            onChange={(e) => setLng(e.target.value)} placeholder="-39.5493" />
        </label>
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Zoom (3–20)</span>
          <input className="installer-input" type="number" min={3} max={20} value={zoom}
            onChange={(e) => setZoom(e.target.value)} />
        </label>
        {current && (current.lat != null) && (
          <button type="button"
            onClick={() => { setLat(current.lat); setLng(current.lng); setZoom(current.zoom || 13); }}
            className="text-[11px] text-noc-accent underline">
            Capturar a posição atual do mapa ({current.lat}, {current.lng})
          </button>
        )}
        {errorMsg && (
          <div className="text-[11px] text-noc-err">{errorMsg}</div>
        )}
        <div className="flex justify-between gap-2 pt-1">
          {onClear ? (
            <button type="button" onClick={onClear} disabled={saving}
              className="h-8 px-3 rounded-lg text-xs border border-noc-border text-noc-muted disabled:opacity-50">
              Voltar ao enquadramento da frota
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={saving}
              className="h-8 px-3 rounded-lg text-xs border border-noc-border text-noc-muted disabled:opacity-50">
              Cancelar
            </button>
            <button type="button" onClick={submit} disabled={saving}
              className="h-8 px-3 rounded-lg text-xs font-semibold bg-noc-accent/15 text-noc-accent border border-noc-accent/40 disabled:opacity-50">
              {saving ? "Salvando…" : "Salvar centro"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
